(() => {
  'use strict';

  if (window.NawahShippingUpsell?.version) {
    window.NawahShippingUpsell.refresh?.();
    return;
  }

  const THRESHOLD = 75000;
  const MAX_MODAL_CANDIDATES = 4;
  const MAX_CART_CANDIDATES = 8;
  const ROOT = window.Shopify?.routes?.root || '/';
  const state = {
    checkoutUrl: `${ROOT}checkout`,
    lastTrigger: null,
    refreshTimer: null,
    observer: null,
  };
  let cachedCopy = null;

  function copy() {
    if (cachedCopy) return cachedCopy;
    const node = document.querySelector('[data-nawah-shipping-copy]');
    if (!node) return {};
    try {
      cachedCopy = JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('NAWAH shipping copy could not be read.', error);
      cachedCopy = {};
    }
    return cachedCopy;
  }

  function withAmount(template, amount) {
    return String(template || '').replace(/__AMOUNT__/g, amount);
  }

  function formatMoney(cents) {
    const amount = Math.max(0, Number(cents || 0)) / 100;
    const locale = (document.documentElement.lang || 'en').toLowerCase().startsWith('ar') ? 'ar-EG' : 'en-EG';
    const value = new Intl.NumberFormat(locale, {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return withAmount(copy().money, value);
  }

  function shippingGap(total, threshold = THRESHOLD) {
    return Math.max(0, Number(threshold || 0) - Math.max(0, Number(total || 0)));
  }

  function rankCandidates(products, gap, inCartIds = new Set()) {
    return products
      .filter((product) => {
        if (!product || product.available === false || product.excluded) return false;
        if (Number(product.price) <= 0) return false;
        return !inCartIds.has(String(product.id));
      })
      .map((product, index) => ({
        ...product,
        _index: Number.isFinite(Number(product.order)) ? Number(product.order) : index,
        _price: Number(product.price),
      }))
      .sort((a, b) => {
        if (gap <= 0) return a._index - b._index;
        const aCloses = a._price >= gap;
        const bCloses = b._price >= gap;
        if (aCloses !== bCloses) return aCloses ? -1 : 1;
        if (aCloses) {
          const aOver = a._price - gap;
          const bOver = b._price - gap;
          if (aOver !== bOver) return aOver - bOver;
        } else {
          const aShort = gap - a._price;
          const bShort = gap - b._price;
          if (aShort !== bShort) return aShort - bShort;
        }
        if (a._price !== b._price) return a._price - b._price;
        return a._index - b._index;
      });
  }

  function pool() {
    const node = document.querySelector('[data-nawah-shipping-pool]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('NAWAH shipping candidate pool could not be read.', error);
      return [];
    }
  }

  function cartUrl() {
    const base = window.routes?.cart_url || `${ROOT}cart`;
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  async function getCart() {
    const response = await fetch(cartUrl(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Unable to read cart (${response.status})`);
    return response.json();
  }

  function inCartProductIds(cart) {
    return new Set((cart?.items || []).map((item) => String(item.product_id)));
  }

  function updateProgressNodes(cart) {
    const gap = shippingGap(cart?.total_price);
    const percentage = Math.min(100, Math.max(0, (Number(cart?.total_price || 0) / THRESHOLD) * 100));
    const text = copy();

    document.querySelectorAll('[data-nawah-shipping-progress]').forEach((root) => {
      root.dataset.shippingGap = String(gap);
      const status = root.querySelector('[data-nawah-shipping-status]');
      const fill = root.querySelector('[data-nawah-shipping-fill]');
      const bar = root.querySelector('[data-nawah-shipping-bar]');
      if (status) status.textContent = gap > 0 ? withAmount(text.away, formatMoney(gap)) : text.unlocked;
      if (fill) fill.style.width = `${percentage}%`;
      if (bar) {
        bar.setAttribute('aria-valuenow', String(Math.round(percentage)));
        bar.setAttribute('aria-valuetext', gap > 0 ? withAmount(text.away, formatMoney(gap)) : text.unlocked);
      }
    });

    document.querySelectorAll('[data-nawah-recs-title]').forEach((heading) => {
      heading.textContent = gap > 0 ? text.recTitle : text.defaultRecTitle;
    });

    return gap;
  }

  function updateCartRecommendations(cart, gap) {
    const inCart = inCartProductIds(cart);

    document.querySelectorAll('[data-nawah-cart-recommendations]').forEach((root) => {
      const track = root.querySelector('[data-nawah-recs-track]');
      if (!track) return;

      const candidates = [...track.querySelectorAll('[data-nawah-shipping-candidate]')].map((card, index) => {
        if (!card.dataset.originalOrder) card.dataset.originalOrder = String(index);
        return {
          element: card,
          id: String(card.dataset.productId || ''),
          price: Number(card.dataset.priceCents || 0),
          available: card.dataset.available !== 'false',
          excluded: card.dataset.excluded === 'true',
          order: Number(card.dataset.originalOrder),
        };
      });

      const ranked = rankCandidates(candidates, gap, inCart);
      const target = ranked.slice(0, MAX_CART_CANDIDATES).map((item) => item.element);
      const keep = new Set(target);

      candidates.forEach((item) => {
        if (!keep.has(item.element)) item.element.remove();
      });

      target.forEach((element, index) => {
        if (track.children[index] !== element) {
          track.insertBefore(element, track.children[index] || null);
        }
      });

      root.dataset.recIndex = '0';
      track.style.transform = 'translateX(0%)';
      const controls = root.querySelector('[data-nawah-recs-controls]');
      const previous = root.querySelector('[data-nawah-recs-prev]');
      const next = root.querySelector('[data-nawah-recs-next]');
      const count = track.children.length;
      if (controls) controls.hidden = count <= 1;
      if (previous) previous.disabled = true;
      if (next) next.disabled = count <= 1;

      const empty = root.querySelector('[data-nawah-recs-empty]');
      if (empty) empty.hidden = count > 0;
    });
  }

  function modal() {
    return document.querySelector('[data-nawah-shipping-modal]');
  }

  function renderModal(cart) {
    const root = modal();
    if (!root) return;
    const gap = updateProgressNodes(cart);
    const text = copy();
    const title = root.querySelector('[data-nawah-shipping-modal-title]');
    const message = root.querySelector('[data-nawah-shipping-modal-message]');
    const list = root.querySelector('[data-nawah-shipping-modal-products]');
    const continueButton = root.querySelector('[data-nawah-shipping-checkout]');
    const closeButtons = root.querySelectorAll('[data-nawah-shipping-close]');

    if (title) title.textContent = gap > 0 ? text.modalTitle : text.unlocked;
    if (message) message.textContent = gap > 0 ? withAmount(text.modalBody, formatMoney(gap)) : text.modalUnlocked;
    if (continueButton) continueButton.textContent = text.continueCheckout;
    closeButtons.forEach((button) => {
      if (button.hasAttribute('aria-label')) button.setAttribute('aria-label', text.close);
      const label = button.querySelector('[data-nawah-shipping-close-label]');
      if (label) label.textContent = text.keepShopping;
    });

    if (!list) return;
    const ranked = rankCandidates(pool(), gap, inCartProductIds(cart)).slice(0, MAX_MODAL_CANDIDATES);

    if (gap <= 0 || ranked.length === 0) {
      list.innerHTML = `<p class="nawah-shipping-modal__empty">${text.empty}</p>`;
      return;
    }

    list.innerHTML = ranked.map((product) => {
      const image = product.image
        ? `<img src="${String(product.image).replace(/"/g, '&quot;')}" alt="" loading="lazy" width="160" height="160">`
        : '<span class="nawah-shipping-product__placeholder" aria-hidden="true"></span>';
      const safeTitle = String(product.title || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const safeUrl = String(product.url || '#').replace(/"/g, '&quot;');
      const needsChoice = product.requiresGiftChoice || product.hasVariants;
      const action = needsChoice
        ? `<a class="nawah-shipping-product__action" href="${safeUrl}">${text.choose}</a>`
        : `<button class="nawah-shipping-product__action" type="button"
              data-nawah-quick-add
              data-product-handle="${String(product.handle || '').replace(/"/g, '&quot;')}"
              data-product-url="${safeUrl}"
              data-variant-id="${String(product.variantId || '').replace(/"/g, '&quot;')}"
              data-has-variants="false"
              data-requires-gift-choice="false">${text.add}</button>`;

      return `<article class="nawah-shipping-product" data-product-id="${String(product.id || '').replace(/"/g, '&quot;')}">
        <a class="nawah-shipping-product__media" href="${safeUrl}">${image}</a>
        <div class="nawah-shipping-product__copy">
          <a class="nawah-shipping-product__title" href="${safeUrl}">${safeTitle}</a>
          <strong>${formatMoney(product.price)}</strong>
          ${action}
        </div>
      </article>`;
    }).join('');
  }

  function closeModal({ restoreFocus = true } = {}) {
    const root = modal();
    if (!root || root.hidden) return;
    root.hidden = true;
    document.body.classList.remove('nawah-shipping-modal-open');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(restoreFocus ? state.lastTrigger : null);
    } else if (restoreFocus) {
      state.lastTrigger?.focus?.({ preventScroll: true });
    }
    state.lastTrigger = null;
  }

  function openModal(cart, checkoutUrl) {
    const root = modal();
    if (!root) return false;
    state.checkoutUrl = checkoutUrl || `${ROOT}checkout`;
    state.lastTrigger = document.activeElement;
    renderModal(cart);
    root.hidden = false;
    document.body.classList.add('nawah-shipping-modal-open');
    requestAnimationFrame(() => {
      const dialog = root.querySelector('[data-nawah-shipping-dialog]');
      const closeButton = root.querySelector('[data-nawah-shipping-close]');
      if (dialog && typeof trapFocus === 'function') trapFocus(dialog, closeButton || dialog);
      else dialog?.focus?.({ preventScroll: true });
    });
    return true;
  }

  async function gateCheckout(checkoutUrl) {
    try {
      const cart = await getCart();
      const gap = shippingGap(cart?.total_price);
      updateProgressNodes(cart);
      updateCartRecommendations(cart, gap);
      if (gap <= 0) return true;
      return !openModal(cart, checkoutUrl);
    } catch (error) {
      console.warn('NAWAH free-shipping gate is unavailable; checkout will continue.', error);
      return true;
    }
  }

  async function refresh() {
    try {
      const cart = await getCart();
      const gap = updateProgressNodes(cart);
      updateCartRecommendations(cart, gap);
      const root = modal();
      if (root && !root.hidden) renderModal(cart);
      document.dispatchEvent(new CustomEvent('nawah:shipping-upsell-refreshed', { detail: { cart, gap } }));
      return cart;
    } catch (error) {
      console.warn('NAWAH shipping progress could not refresh.', error);
      return null;
    }
  }

  function scheduleRefresh(delay = 120) {
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = window.setTimeout(refresh, delay);
  }

  function bind() {
    document.addEventListener('click', (event) => {
      const checkout = event.target.closest('[data-nawah-shipping-checkout]');
      if (checkout) {
        event.preventDefault();
        window.location.assign(state.checkoutUrl || `${ROOT}checkout`);
        return;
      }

      const close = event.target.closest('[data-nawah-shipping-close]');
      if (close) {
        event.preventDefault();
        closeModal();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal() && !modal().hidden) closeModal();
    });

    document.addEventListener('nawah:cart-updated', () => scheduleRefresh(80));

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleRefresh(120));
      } catch (error) {
        console.warn('NAWAH shipping cart subscription unavailable.', error);
      }
    }

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      state.observer = new MutationObserver(() => scheduleRefresh(160));
      state.observer.observe(drawer, { childList: true, subtree: true });
    }
  }

  function init() {
    bind();
    refresh();
  }

  window.NawahShippingUpsell = {
    version: '1.0.0',
    threshold: THRESHOLD,
    shippingGap,
    rankCandidates,
    gateCheckout,
    refresh,
    close: closeModal,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
